import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { fetchStudentsForPrincipal } from '../../services/principalStudents';
import { fetchTimeSlotsForStudents } from '../../services/timeSlotService';
import { fetchActiveAnnouncements } from '../../services/announcementService';
import { expandUpcomingLessons } from '../../utils/occurrenceExpansion';
import { formatTime12Hour } from '../../utils/schedulingUtils';
import { LOCATIONS } from '../EmployeePortal/Scheduling/locations';
import { upcomingImportantDates, daysAwayLabel, seasonLookaheadDays } from '../../data/seasonData';
import PaymentMethodNudge from './PaymentMethodNudge';
import LatestRecapCard from './LatestRecapCard';
import { isStudentPrincipal } from '../../services/principalStudents';
import {
	PageWrap,
	Card,
	SectionHead,
	QuietState,
	studentDisplayName,
} from './customerUi';

/* Home — the customer hub (rewrite of the old card-grid dashboard). Three
   surfaces, top to bottom: next important dates (prominent, days-away), latest
   studio announcements (read-only, "see all"), and each student's next lesson at
   a glance. Every block keeps a calm state when empty — it never renders blank.

   Data sources (STEP 0):
   - Important dates: shared season module (hard-coded handbook recitals +
     rehearsals). `events` is staff-only and closures aren't modeled, so there's
     no customer-readable live source — the block surfaces upcoming season dates
     and points at the full Season calendar for the rest.
   - Announcements: the active/non-expired set (customer read allowed by rules).
   - This week: the two familyId-scoped reads (students, then their timeSlots),
     expanded into the next occurrence per student — same engine as Schedule. */

const DOW_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const locationName = (value) => {
	const match = LOCATIONS.find((l) => l.value === value);
	return match ? match.name : value || '';
};

const CustomerDashboard = () => {
	const { currentUser, userProfile } = useAuth();
	const navigate = useNavigate();
	const familyId = userProfile?.familyId || null;
	// P2b: a student principal holds BOTH scalars, so familyId alone no longer
	// identifies the read. Depend on the primitives, not on userProfile's
	// object identity, and let the resolver issue the per-principal query.
	const studentId = userProfile?.studentId || null;
	const role = userProfile?.role || null;

	const [students, setStudents] = useState([]);
	const [slots, setSlots] = useState([]);
	const [announcements, setAnnouncements] = useState([]);

	// Recitals + studio closures, next 3 — a closure counts until its end date.
	const dates = upcomingImportantDates(new Date(), 3);
	// Subtitle for the important-dates row: a recital shows its venue; a closure
	// shows its inclusive date range ("Nov 25 – Nov 29").
	const importantSubtitle = (d) => {
		if (d.type === 'recital') return d.location || '';
		const s = new Date(d.start + 'T00:00:00');
		if (d.start === d.end) return 'Studio closed';
		const e = new Date(d.end + 'T00:00:00');
		const opt = { month: 'short', day: 'numeric' };
		return `Closed ${s.toLocaleDateString('en-US', opt)} – ${e.toLocaleDateString('en-US', opt)}`;
	};

	const firstName =
		userProfile?.firstName ||
		currentUser?.displayName?.split(' ')[0] ||
		'there';

	// Announcements are family-independent — fetch regardless of link state.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const list = await fetchActiveAnnouncements();
				if (!cancelled) setAnnouncements(list.slice(0, 2));
			} catch (err) {
				console.error('Error loading announcements:', err);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	// Family schedule — only when linked (the two rule-scoped reads).
	useEffect(() => {
		if (!familyId) return undefined;
		let cancelled = false;
		(async () => {
			try {
				const kids = await fetchStudentsForPrincipal({ role, familyId, studentId });
				const fetched = await fetchTimeSlotsForStudents(kids.map((k) => k.id));
				if (cancelled) return;
				setStudents(kids);
				setSlots(fetched.filter((s) => s.status === 'assigned'));
			} catch (err) {
				console.error('Error loading family schedule:', err);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [role, familyId, studentId]);

	const today = new Date();
	today.setHours(0, 0, 0, 0);
	// Season-wide window, same as Schedule. This block only takes the SOONEST
	// occurrence per student, so the span costs nothing to widen — but at the old
	// fixed 60 days a student whose next lesson sat further out read as "no
	// upcoming lesson" here while Schedule listed it, which is the cross-surface
	// disagreement the shared expander exists to prevent.
	const upcoming = expandUpcomingLessons(slots, today, seasonLookaheadDays(today));

	// The soonest upcoming occurrence per student (or null if none placed).
	const nextByStudent = students.map((s) => {
		const next = upcoming
			.filter((o) => o.studentId === s.id)
			.sort((a, b) => a._date - b._date)[0];
		return { student: s, next };
	});

	const dateHeading = new Date().toLocaleDateString('en-US', {
		weekday: 'long',
		month: 'long',
		day: 'numeric',
	});

	// ---- Pre-link state: nothing to navigate; prompt to link the family. ----
	if (!familyId) {
		return (
			<PageWrap>
				<Card style={{ padding: '26px 20px 24px', textAlign: 'center', marginTop: '2rem' }}>
					<span
						style={{
							display: 'inline-flex',
							width: 60,
							height: 60,
							borderRadius: '50%',
							background:
								'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 14%, var(--surface-card, #fff))',
							color: 'var(--cyv-teal-600, #2f7d77)',
							alignItems: 'center',
							justifyContent: 'center',
							marginBottom: 14,
						}}>
						<i className='fa-sharp fa-solid fa-link' style={{ fontSize: 21 }} aria-hidden='true' />
					</span>
					<h1
						style={{
							fontFamily: 'var(--font-serif)',
							fontSize: '1.35rem',
							fontWeight: 'var(--fw-semibold, 600)',
							color: 'var(--text-heading, #384971)',
							margin: '0 0 8px',
						}}>
						Welcome to Color Your Voice
					</h1>
					<p
						style={{
							fontSize: '0.92rem',
							color: 'var(--text-muted, #7b8499)',
							lineHeight: 1.6,
							maxWidth: 300,
							margin: '0 auto',
						}}>
						You’re signed in, but this account isn’t connected to a family yet.
						Use the link from your welcome email, or{' '}
						<a href='/contact'>contact us</a> and we’ll get you set up.
					</p>
				</Card>
			</PageWrap>
		);
	}

	// ---- Linked hub. ----
	return (
		<PageWrap>
			{/* Greeting */}
			<div style={{ marginBottom: 16 }}>
				<div
					style={{
						fontSize: '0.68rem',
						fontWeight: 'var(--fw-bold, 700)',
						letterSpacing: '0.14em',
						textTransform: 'uppercase',
						color: 'var(--text-muted, #a6adba)',
					}}>
					{dateHeading}
				</div>
				<div
					style={{
						fontFamily: 'var(--font-serif)',
						fontSize: '1.6rem',
						fontWeight: 'var(--fw-semibold, 600)',
						color: 'var(--text-heading, #384971)',
						lineHeight: 1.1,
						marginTop: 4,
					}}>
					Hi, {firstName}
				</div>
				<div style={{ fontSize: '0.9rem', color: 'var(--text-muted, #7b8499)', marginTop: 5 }}>
					Here’s what’s coming up for your family.
				</div>
			</div>

			{/* Tuition owed + nothing on file: the one state where a parent needs
			    More → Payments and cannot find it. Renders nothing otherwise. */}
			<PaymentMethodNudge familyId={familyId} userProfile={userProfile} />

			{/* Important dates */}
			<Card style={{ marginBottom: 14 }}>
				<SectionHead
					icon='fa-star'
					label='Important dates'
					color='var(--cyv-date, #ed7749)'
					action='Full calendar'
					onAction={() => navigate('/portal/customer/calendar')}
				/>
				{dates.length > 0 ? (
					dates.map((d, i) => (
						<div
							key={`${d.type}-${d.start}`}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 12,
								padding: '11px 15px',
								borderBottom:
									i < dates.length - 1
										? '1px solid var(--cyv-line, #f0eae1)'
										: 'none',
							}}>
							<span
								style={{
									width: 44,
									height: 44,
									borderRadius: 12,
									background:
										'color-mix(in srgb, var(--cyv-date, #ed7749) 12%, var(--surface-card, #fff))',
									color: 'var(--cyv-date, #d9776a)',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									justifyContent: 'center',
									flex: '0 0 auto',
									lineHeight: 1,
								}}>
								<span style={{ fontFamily: 'var(--font-serif)', fontSize: 17, fontWeight: 700 }}>
									{new Date(d.start + 'T00:00:00').getDate()}
								</span>
								<span style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.08em', marginTop: 2 }}>
									{new Date(d.start + 'T00:00:00')
										.toLocaleDateString('en-US', { month: 'short' })
										.toUpperCase()}
								</span>
							</span>
							<span style={{ flex: 1, minWidth: 0 }}>
								<span
									style={{
										display: 'block',
										fontSize: '0.9rem',
										fontWeight: 'var(--fw-bold, 700)',
										color: 'var(--text-heading, #384971)',
									}}>
									{d.name}
								</span>
								<span
									style={{
										display: 'block',
										fontSize: '0.78rem',
										color: 'var(--text-muted, #7b8499)',
										marginTop: 2,
									}}>
									{importantSubtitle(d)}
								</span>
							</span>
							<span
								style={{
									whiteSpace: 'nowrap',
									fontSize: '0.7rem',
									fontWeight: 'var(--fw-bold, 700)',
									color: 'var(--cyv-date, #d9776a)',
									background:
										'color-mix(in srgb, var(--cyv-date, #ed7749) 12%, var(--surface-card, #fff))',
									borderRadius: 999,
									padding: '4px 9px',
									flex: '0 0 auto',
								}}>
								{daysAwayLabel(d.daysAway)}
							</span>
						</div>
					))
				) : (
					<QuietState icon='fa-calendar'>
						No upcoming dates yet — recitals, rehearsals and breaks show on the{' '}
						<Link to='/portal/customer/calendar'>Season calendar</Link>.
					</QuietState>
				)}
			</Card>

			{/* From the studio */}
			<Card style={{ marginBottom: 14 }}>
				<SectionHead
					icon='fa-bullhorn'
					label='From the studio'
					action={announcements.length > 0 ? 'See all' : undefined}
					onAction={() => navigate('/portal/customer/announcements')}
				/>
				{announcements.length > 0 ? (
					announcements.map((a, i) => (
						<div
							key={a.id}
							style={{
								padding: '2px 15px 13px',
								paddingTop: i === 0 ? 0 : 12,
								borderBottom:
									i < announcements.length - 1
										? '1px solid var(--cyv-line, #f0eae1)'
										: 'none',
							}}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
								{i === 0 && (
									<span
										style={{
											width: 7,
											height: 7,
											borderRadius: '50%',
											background: 'var(--cyv-teal-600, #54ada7)',
											flex: '0 0 auto',
										}}
									/>
								)}
								<span
									style={{
										fontSize: '0.9rem',
										fontWeight: 'var(--fw-bold, 700)',
										color: 'var(--text-heading, #384971)',
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}>
									{a.title || 'Studio announcement'}
								</span>
							</div>
							<div
								style={{
									fontSize: '0.8rem',
									color: 'var(--text-body, #5a6378)',
									lineHeight: 1.5,
									marginTop: 4,
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
								}}>
								{a.message}
							</div>
						</div>
					))
				) : (
					<QuietState icon='fa-feather'>
						Nothing new right now — studio news lands here.
					</QuietState>
				)}
			</Card>

			{/* This week — per-student next lesson */}
			<Card>
				<SectionHead
					icon='fa-music'
					label='This week'
					action='Schedule'
					onAction={() => navigate('/portal/customer/schedule')}
				/>
				{nextByStudent.length > 0 ? (
					nextByStudent.map(({ student, next }, i) => (
						<div
							key={student.id}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 12,
								padding: '11px 15px',
								borderBottom:
									i < nextByStudent.length - 1
										? '1px solid var(--cyv-line, #f0eae1)'
										: 'none',
							}}>
							<span style={{ flex: 1, minWidth: 0 }}>
								<span
									style={{
										display: 'block',
										fontSize: '0.9rem',
										fontWeight: 'var(--fw-bold, 700)',
										color: 'var(--text-heading, #384971)',
									}}>
									{studentDisplayName(student)}
									{next
										? ` — ${DOW_ABBR[next._date.getDay()]} ${formatTime12Hour(next.startTime)}`
										: ''}
								</span>
								<span
									style={{
										display: 'block',
										fontSize: '0.78rem',
										color: 'var(--text-muted, #7b8499)',
										marginTop: 2,
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}>
									{next
										? locationName(next.location)
										: 'No lesson placed yet'}
								</span>
							</span>
							{next && (
								<span
									style={{
										whiteSpace: 'nowrap',
										fontSize: '0.7rem',
										fontWeight: 'var(--fw-bold, 700)',
										color: 'var(--cyv-teal-600, #2f7d77)',
										background:
											'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 14%, var(--surface-card, #fff))',
										borderRadius: 999,
										padding: '4px 9px',
										flex: '0 0 auto',
									}}>
									{daysAwayLabel(
										Math.round((next._date - today) / 86400000)
									)}
								</span>
							)}
						</div>
					))
				) : (
					<QuietState icon='fa-calendar'>
						No lessons placed yet — they’ll show up here once your weekly time is
						set.
					</QuietState>
				)}
			</Card>

			{/* Latest lesson recap, under the next lesson (design pass 2, change
			    11): what happened last time sits beside what is coming next.
			    Renders nothing until the students are known. */}
			<div style={{ marginTop: 14 }}>
				<LatestRecapCard students={students} studentOnly={isStudentPrincipal(userProfile)} />
			</div>
		</PageWrap>
	);
};

export default CustomerDashboard;
